import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/layout/PageHeader';
import { TokenChipInput } from '../../components/ui/TokenChipInput';
import { cn } from '../../lib/utils';
import { presetToRange, previousRange, type DateRange } from '../../lib/reportingDateRange';
import { resolvePullFreshnessNotice } from '../../lib/reporting/pullFreshness';
import type {
  ReportingBreakdownFilters,
  ReportingDatePreset,
  ReportingFilters,
} from '../../types/reporting';
import {
  getFxRates,
  getReportingAttributionHealth,
  getReportingBreakdown,
  getReportingDataFreshness,
  getReportingFilterOptions,
  getReportingSummary,
} from './api';
import { patchMyPreferences } from '../accounts';
import { useCurrentUser } from '../auth';
import { AttributionHealthPanel } from './AttributionHealthPanel';
import { BreakdownExplorer } from './BreakdownExplorer';
import { DateRangePicker } from './DateRangePicker';
import { PullFreshnessNote } from './PullFreshnessNote';
import { buildHeroCards } from './heroCards';
import { HeroStatCard } from './HeroStatCard';
import { ReportingChartsMenu } from './ReportingChartsMenu';
import { ReportingChartGrid, type ChartPanel } from './ReportingChartGrid';
import {
  AddWidgetButton,
  buildWidgetPanels,
  type WidgetDataSource,
} from './ReportingWidgets';
import { ReportingViewActions } from './ReportingViewActions';
import type { ReportingWidget } from '../../lib/reporting/widgets';
import { canvasPanelKey, mergeStoredLayout } from '../../lib/reporting/chartLayout';
import { ReportingTrendChart } from './ReportingTrendChart';
import {
  CHART_METRICS,
  buildTrendSeries,
  resolveTrendMetric,
  sumAllTotals,
  DEFAULT_TREND_METRIC,
} from './trendSeries';
import {
  buildMetaDimensionalData,
  isMetaWidgetDimension,
  META_WIDGET_DIMENSIONS,
} from './metaWidgetDimensions';
import { firstWidgetDimension, hasDimensionalWidget } from '../../lib/reporting/widgetSlices';
import { TREND_GROUPINGS, type TrendGrouping } from './chartDate';
import {
  decodeChartsState,
  encodeChartsState,
  MAX_MULTI_TREND_SERIES,
  REPORTING_CHARTS_GRID_KEY,
  reportingCanvasPanelKeys,
  sanitizeSelected,
  sanitizeTrendMetrics,
  styleForMetric,
  type ChartsState,
  type ChartStyle,
} from './chartPrefs';
import { MultiTrendChart } from './MultiTrendChart';
import { buildMetaMultiTrend } from './multiTrendSeries';
import { SeriesPicker } from './SeriesPicker';
import {
  resolveDisplayCurrency,
  resolveSummaryView,
  showCurrencyToggle,
} from './currencyScope';
import {
  isNativeDisplay,
  normalizeDisplayCurrency,
} from '../../lib/reporting/currencyConversion';
import { ConversionNote, DisplayCurrencySelect } from './DisplayCurrencySelect';
import {
  EMPTY_FILTER_SELECTION,
  ReportingFilterBar,
  type ReportingFilterSelection,
} from './ReportingFilterBar';

const GROUPING_LABEL: Record<TrendGrouping, string> = {
  daily: 'daily',
  weekly: 'weekly',
  monthly: 'monthly',
};

// The 8 hero stats (lowercase labels, spec §5 order) — used for the loading
// placeholder grid before values arrive. Reach + Frequency were removed (§10).
const HERO_LABELS = [
  'spend',
  'leads',
  'meta leads',
  'website leads',
  'cost per lead',
  'cost per meta lead',
  'impressions',
  'ctr',
] as const;

function activeFilterCount(s: ReportingFilterSelection): number {
  return (
    s.campaignTypes.length +
    s.brandAccountIds.length +
    s.groupAccountIds.length +
    s.industries.length +
    s.accountManagerIds.length +
    s.countries.length +
    s.currencies.length +
    s.accountIds.length +
    (s.campaignSearchTokens.length > 0 ? 1 : 0) +
    (s.adsetName.length > 0 ? 1 : 0) +
    (s.adName.length > 0 ? 1 : 0)
  );
}

export function ReportingPage() {
  const user = useCurrentUser();
  const qc = useQueryClient();
  const [preset, setPreset] = useState<ReportingDatePreset>('past_30');
  const [custom, setCustom] = useState<DateRange>(() => presetToRange('past_30', new Date()));
  // A `?accountId=` deep link (the Advertising Home's alert/book rows) seeds the
  // Account filter ONCE at mount — read lazily, never re-applied, so it can't
  // stomp a selection the user changes afterwards.
  const [selection, setSelection] = useState<ReportingFilterSelection>(() => {
    const seeded = new URLSearchParams(window.location.search).get('accountId');
    return seeded ? { ...EMPTY_FILTER_SELECTION, accountIds: [seeded] } : EMPTY_FILTER_SELECTION;
  });
  // The hero card that drives the trend chart below (default Spend).
  const [trendMetric, setTrendMetric] = useState<string>(DEFAULT_TREND_METRIC);
  // The display-currency toggle (client-side view scope, NOT the query filter).
  // null = follow the default (highest-spend currency); resolved against scope.
  const [pickedCurrency, setPickedCurrency] = useState<string | null>(null);

  // Chart section config (which cards, grouping, big-chart minimise, per-metric
  // style) — persisted per-user via gridPreferences ('reporting-charts').
  const [chartsState, setChartsState] = useState<ChartsState>(() =>
    decodeChartsState(user.gridPreferences?.[REPORTING_CHARTS_GRID_KEY]),
  );
  const saveCharts = useMutation({
    mutationFn: (next: ChartsState) =>
      patchMyPreferences(REPORTING_CHARTS_GRID_KEY, encodeChartsState(next)),
    onSuccess: (u) => qc.setQueryData(['me'], u),
  });
  const commitCharts = (next: ChartsState) => {
    setChartsState(next);
    saveCharts.mutate(next, {
      onError: (err) => console.error('Failed to save chart preferences', err),
    });
  };
  const setSelectedCharts = (keys: string[]) =>
    commitCharts({ ...chartsState, selected: sanitizeSelected(keys) });
  const setGrouping = (grouping: TrendGrouping) => commitCharts({ ...chartsState, grouping });
  const toggleMinimise = () => commitCharts({ ...chartsState, minimised: !chartsState.minimised });
  const setMetricStyle = (key: string, style: ChartStyle) =>
    commitCharts({ ...chartsState, styles: { ...chartsState.styles, [key]: style } });
  const setMultiTrend = (multiTrend: boolean) => commitCharts({ ...chartsState, multiTrend });
  const setDisplayCurrency = (displayCurrency: string) =>
    commitCharts({ ...chartsState, displayCurrency });
  const setTrendMetrics = (keys: string[]) =>
    commitCharts({ ...chartsState, trendMetrics: sanitizeTrendMetrics(keys) });

  // Effective range: presets resolve live; custom uses the picked inputs.
  const range = useMemo(() => {
    try {
      return presetToRange(preset, new Date(), custom);
    } catch {
      return custom;
    }
  }, [preset, custom]);

  const optionsQuery = useQuery({
    queryKey: ['reporting-filter-options'],
    queryFn: getReportingFilterOptions,
    staleTime: 5 * 60 * 1000,
  });

  // The agency rate snapshot. A MONTHLY table, so a long staleTime — and the
  // SAME query key the Google dashboard uses, so the two share one cached read
  // and structurally cannot convert at different rates.
  const fxQuery = useQuery({
    queryKey: ['reporting-fx-rates'],
    queryFn: getFxRates,
    staleTime: 30 * 60 * 1000,
  });
  const fxSnapshot = fxQuery.data?.rates ?? null;

  // When the Meta pull last landed. ⚠️ Independent of the filters and the range —
  // "when did we last look" is one fact about the pipeline, so it is fetched ONCE
  // with a long staleTime rather than riding the summary on every filter change.
  const freshnessQuery = useQuery({
    queryKey: ['reporting-data-freshness'],
    queryFn: getReportingDataFreshness,
    staleTime: 5 * 60 * 1000,
  });

  const rangeValid = Boolean(range.from && range.to && range.from <= range.to);

  // Effective filters (only non-empty arrays reach the query string). The
  // campaign-name tokens commit discretely (Enter / comma / blur), so no
  // keystroke debounce is needed — the query fires on token commit.
  const filters = useMemo<ReportingFilters>(() => {
    const nonEmpty = (a: string[]) => (a.length > 0 ? a : undefined);
    return {
      from: range.from,
      to: range.to,
      campaignTypes: nonEmpty(selection.campaignTypes),
      brandAccountIds: nonEmpty(selection.brandAccountIds),
      groupAccountIds: nonEmpty(selection.groupAccountIds),
      industries: nonEmpty(selection.industries),
      accountManagerIds: nonEmpty(selection.accountManagerIds),
      countries: nonEmpty(selection.countries),
      currencies: nonEmpty(selection.currencies),
      accountIds: nonEmpty(selection.accountIds),
      campaignSearchTokens: nonEmpty(selection.campaignSearchTokens),
      nameFilters: { adGroupName: selection.adsetName, adName: selection.adName },
    };
  }, [range, selection]);

  // §3b attribution health — date + currency + campaign tokens scoped only; the
  // location/account filters don't apply to unattributed/collision rows.
  const healthFilters = useMemo(
    () => ({
      from: range.from,
      to: range.to,
      currencies: selection.currencies.length > 0 ? selection.currencies : undefined,
      campaignSearchTokens:
        selection.campaignSearchTokens.length > 0 ? selection.campaignSearchTokens : undefined,
      nameFilters: { adGroupName: selection.adsetName, adName: selection.adName },
    }),
    [range, selection.currencies, selection.campaignSearchTokens, selection.adsetName, selection.adName],
  );
  const healthQuery = useQuery({
    queryKey: ['reporting-attribution-health', healthFilters],
    queryFn: () => getReportingAttributionHealth(healthFilters),
    enabled: rangeValid,
    placeholderData: (prev) => prev,
  });

  const summaryQuery = useQuery({
    queryKey: ['reporting-summary', filters],
    queryFn: () => getReportingSummary(filters),
    enabled: rangeValid,
    placeholderData: (prev) => prev,
  });

  // Previous equivalent period → the vs-previous delta.
  const prevFilters = useMemo<ReportingFilters | null>(() => {
    if (!rangeValid) return null;
    const pr = previousRange(range);
    return { ...filters, from: pr.from, to: pr.to };
  }, [filters, range, rangeValid]);

  const prevQuery = useQuery({
    queryKey: ['reporting-summary-prev', prevFilters],
    queryFn: () => getReportingSummary(prevFilters!),
    enabled: rangeValid && prevFilters !== null,
    placeholderData: (prev) => prev,
  });

  // Display-currency scoping: re-derive the whole view (hero + mini-charts + big
  // chart) from the selected currency's rows only — instant, no refetch. The
  // toggle shows only for multi-currency scope; the current + previous summaries
  // are scoped to the SAME currency so deltas stay per-currency.
  const rawSummary = summaryQuery.data ?? null;
  // The NATIVE currency the Native-mode toggle scopes to — unchanged behaviour.
  const nativeCurrency = rawSummary ? resolveDisplayCurrency(rawSummary, pickedCurrency) : null;
  // ⚠️ The AGENCY display currency, resolved against the LIVE rate table: a
  // stored code the table can no longer reach degrades to Native rather than
  // showing one currency's figures under another's label.
  const displayCurrency = normalizeDisplayCurrency(chartsState.displayCurrency, fxSnapshot);
  const converting = !isNativeDisplay(displayCurrency);
  // ONE branch for how a multi-currency summary collapses: scope to a native
  // currency, or convert-and-blend into the display one. See resolveSummaryView.
  const scoped = rawSummary
    ? resolveSummaryView(rawSummary, displayCurrency, fxSnapshot, nativeCurrency)
    : null;
  const scopedPrev = prevQuery.data
    ? (resolveSummaryView(prevQuery.data, displayCurrency, fxSnapshot, nativeCurrency) ?? undefined)
    : undefined;

  // The active big-chart metric: the clicked card, clamped to the currently
  // selected cards (removing the plotted card falls back to the first selected).
  const selectedKeys = chartsState.selected;
  const activeMetric = selectedKeys.includes(trendMetric)
    ? trendMetric
    : (selectedKeys[0] ?? DEFAULT_TREND_METRIC);
  const activeStyle = styleForMetric(chartsState.styles, activeMetric);

  const cards = scoped ? buildHeroCards(scoped, scopedPrev, selectedKeys) : null;
  const trendData = scoped ? buildTrendSeries(scoped, activeMetric, chartsState.grouping) : null;

  // The KPI tile panels. One per selected hero card, in the chart menu's order —
  // 3 of 12 columns each, so the packer's row-wrap reproduces exactly the
  // `lg:grid-cols-4` row the tiles rendered in before.
  const tilePanels = useMemo<ChartPanel[]>(
    () =>
      cards && scoped
        ? cards.map((card) => ({
            key: canvasPanelKey('tile', card.key),
            title: card.label,
            defaultW: 3,
            defaultH: 4,
            // A tile may legitimately go narrower than a chart (6-up), and
            // shorter once its mini-chart is squeezed out.
            minW: 2,
            minH: 3,
            // The grip lives in the tile's own header beside the style toggle —
            // a strip above each tile in a 4-up row would be visual noise.
            providesOwnGrip: true,
            node: (
              <HeroStatCard
                card={card}
                summary={scoped}
                grouping={chartsState.grouping}
                style={styleForMetric(chartsState.styles, card.key)}
                onStyleChange={setMetricStyle}
                selected={activeMetric === card.key}
                onSelect={setTrendMetric}
              />
            ),
          }))
        : [],
    // setMetricStyle closes over chartsState, which IS a dependency.
    [cards, scoped, chartsState, activeMetric],
  );

  // The multi-metric trend — several metrics on ONE canvas, the capability the
  // selected-metric chart deliberately does not give. Shaped by Meta's adapter
  // onto the SHARED MultiTrendChart that the Google dashboard also renders.
  const multiTrendData = useMemo(
    () => buildMetaMultiTrend(scoped, chartsState.trendMetrics, chartsState.grouping),
    [scoped, chartsState.trendMetrics, chartsState.grouping],
  );

  // The chart panels — the selected-metric trend, and (when ticked) the
  // multi-metric one — as full-width panels on the canvas, freely placeable
  // among the tiles.
  const chartPanels = useMemo<ChartPanel[]>(() => {
    const panels: ChartPanel[] = [];
    if (trendData && !trendData.isEmpty) {
      panels.push({
        key: canvasPanelKey('chart', 'trend'),
        title: `${trendData.metric.label} over time`,
        defaultW: 12,
        defaultH: 8,
        minH: 5,
        node: (
          <ReportingTrendChart
            data={trendData}
            style={activeStyle}
            onStyleChange={(s) => setMetricStyle(activeMetric, s)}
            minimised={chartsState.minimised}
            onToggleMinimise={toggleMinimise}
          />
        ),
      });
    }
    if (chartsState.multiTrend) {
      panels.push({
        key: canvasPanelKey('chart', 'multiTrend'),
        title: 'Trend (multi-metric)',
        defaultW: 12,
        defaultH: 9,
        minH: 6,
        // The series picker rides the panel's TOOLBAR slot, not the body — the
        // same placement the Google chart uses, and it is what keeps the body to
        // exactly one child (which is what the panel-fill CSS targets).
        toolbar: (
          <SeriesPicker
            label="Series"
            options={CHART_METRICS.map((m) => ({ key: m.key, label: m.label }))}
            selected={chartsState.trendMetrics}
            onChange={setTrendMetrics}
            max={MAX_MULTI_TREND_SERIES}
          />
        ),
        node: (
          <MultiTrendChart
            title="Trend (multi-metric)"
            data={multiTrendData}
            loading={summaryQuery.isLoading}
          />
        ),
      });
    }
    return panels;
    // setMetricStyle / toggleMinimise / setTrendMetrics are rebuilt every render
    // but close over chartsState, which IS a dependency — so listing them would
    // add churn without changing when this recomputes.
  }, [trendData, activeStyle, activeMetric, chartsState, multiTrendData, summaryQuery.isLoading]);

  // ⚠️ THE DIMENSIONAL BREAKDOWN IS FETCHED LAZILY, and only when a widget
  // actually asks for one. It is the SAME /breakdown endpoint the explorer below
  // already uses (no new server path) — but paying for another aggregation on
  // every page load to serve a widget nobody added would be wrong. One query
  // serves every dimensional widget on the same dimension.
  const widgetDimension = firstWidgetDimension(chartsState.widgets);
  const widgetBreakdownFilters = useMemo<ReportingBreakdownFilters | null>(() => {
    // A dimensional widget with nothing chosen yet falls back to the first
    // dimension, so the fetch must too or it would sit empty until reconfigured.
    const dimension = widgetDimension ?? (hasDimensionalWidget(chartsState.widgets)
      ? META_WIDGET_DIMENSIONS[0]!.key
      : null);
    if (!dimension || !isMetaWidgetDimension(dimension)) return null;
    return { ...filters, groupBy: [dimension] };
  }, [filters, widgetDimension, chartsState.widgets]);
  const widgetBreakdown = useQuery({
    queryKey: ['reporting-widget-breakdown', widgetBreakdownFilters],
    queryFn: () => getReportingBreakdown(widgetBreakdownFilters!),
    enabled: rangeValid && widgetBreakdownFilters !== null,
    placeholderData: (prev) => prev,
  });

  // ── The user-added widget region ───────────────────────────────────────────
  // Meta's side of the shared framework: the metric catalogue and the resolvers,
  // all reusing the paths the hero cards and the tiles already run through, so a
  // widget and a tile of the same metric can never disagree about the figure.
  const widgetSource = useMemo<WidgetDataSource>(
    () => ({
      metrics: CHART_METRICS.map((m) => ({ key: m.key, label: m.label })),
      number: (metricKey) => {
        // The SAME builder the hero cards use — so the value line is already
        // formatted for the display currency and the delta is already the
        // vs-previous-period one, with the metric's own tone attached.
        const card = scoped ? buildHeroCards(scoped, scopedPrev, [metricKey])[0] : undefined;
        const label = CHART_METRICS.find((m) => m.key === metricKey)?.label ?? metricKey;
        if (!card) {
          // No data in scope yet. "—" is the honest render; a 0 would be a claim.
          return { label, value: '—', unmeasured: true, tone: 'neutral', delta: null };
        }
        return {
          label: card.label,
          value: card.valueLine,
          unmeasured: false,
          tone: card.tone,
          delta: card.delta,
        };
      },
      trend: (metricKey) => {
        const metric = resolveTrendMetric(metricKey);
        // Nothing in scope yet → an explicitly EMPTY series, which the widget
        // renders as "No data in this range." rather than as a flat zero line.
        if (!scoped) {
          return {
            metric,
            format: metric.format,
            currency: null,
            grouping: chartsState.grouping,
            rows: [],
            isEmpty: true,
          };
        }
        return buildTrendSeries(scoped, metricKey, chartsState.grouping);
      },
      style: (metricKey) => styleForMetric(chartsState.styles, metricKey),
      onStyleChange: setMetricStyle,
      // Meta always fetches the previous equivalent period for the tile deltas,
      // so the capability is always there — the delta itself is simply null
      // until that query lands.
      comparisonAvailable: true,
      loading: summaryQuery.isLoading,

      // ── Phase 2 ──────────────────────────────────────────────────────────
      // Meta's funnel vocabulary: LINK clicks (not all-clicks) and LEADS, which
      // are the two the rest of this dashboard reports on — a funnel that used
      // different definitions from the tiles beside it would invite exactly the
      // "these numbers don't match" question.
      funnel: () => {
        const totals = scoped ? sumAllTotals(scoped.trend ?? []) : null;
        return [
          { label: 'Impressions', value: totals ? totals.impressions : null },
          { label: 'Link clicks', value: totals ? totals.linkClicks : null },
          { label: 'Leads', value: totals ? totals.leads : null },
        ];
      },
      percentMetrics: CHART_METRICS.filter((m) => m.format === 'ratio').map((m) => ({
        key: m.key,
        label: m.label,
      })),
      percent: (metricKey) => {
        const metric = resolveTrendMetric(metricKey);
        if (metric.format !== 'ratio' || !scoped) return null;
        return metric.value(sumAllTotals(scoped.trend ?? []));
      },
      dimensions: META_WIDGET_DIMENSIONS,
      dimensional: (metricKey, dimension) =>
        buildMetaDimensionalData({
          metricKey,
          dimension: isMetaWidgetDimension(dimension) ? dimension : 'account',
          rows: widgetBreakdown.data?.rows,
          loading: widgetBreakdown.isLoading,
          // ⚠️ In Native mode a money pie across currencies still REFUSES; with a
          // display currency the rows are converted first and it plots.
          display: displayCurrency,
          snapshot: fxSnapshot,
        }),
    }),
    // setMetricStyle closes over chartsState, which IS a dependency.
    [
      scoped,
      scopedPrev,
      rawSummary,
      chartsState,
      summaryQuery.isLoading,
      widgetBreakdown.data,
      widgetBreakdown.isLoading,
      displayCurrency,
      fxSnapshot,
    ],
  );

  const setWidgets = (widgets: ReportingWidget[]) => commitCharts({ ...chartsState, widgets });

  // ── THE CANVAS ─────────────────────────────────────────────────────────────
  // ONE grid for the whole top section — tiles, the trend chart and the user's
  // own widgets — so every panel is freely placeable among the others rather
  // than only within its own former region.
  //
  // ⚠️ THE ORDER HERE IS THE DEFAULT ARRANGEMENT. Supplied tiles-then-chart-then-
  // widgets at the sizes each used to have, the packer reproduces exactly the
  // stack the separate regions rendered — so the collapse changes nothing on
  // screen until the user drags something.
  const canvasPanels = useMemo<ChartPanel[]>(
    () => [
      ...tilePanels,
      ...chartPanels,
      ...buildWidgetPanels({
        widgets: chartsState.widgets,
        source: widgetSource,
        onWidgetsChange: setWidgets,
      }),
    ],
    // setWidgets closes over chartsState, which IS a dependency.
    [tilePanels, chartPanels, chartsState, widgetSource],
  );

  const filterCount = activeFilterCount(selection);
  const rangeLabel = range.from && range.to ? `${range.from} → ${range.to}` : 'select a range';
  const asOf = summaryQuery.data?.asOf;

  // ⚠️ Only ever a caveat about a range CONTAINING TODAY — see
  // src/lib/reporting/pullFreshness.ts. It is deliberately separate from the `asOf`
  // above: asOf says when the ROWS ON SCREEN were pulled and goes silent when there
  // are none, which is precisely the case ("Today", empty at 8am) where the reader
  // most needs telling whether we have looked yet.
  const freshnessNotice = resolvePullFreshnessNotice({
    range,
    now: new Date(),
    freshness: freshnessQuery.data,
    unavailable: freshnessQuery.isError,
  });

  return (
    <div>
      <PageHeader
        title="meta reporting"
        description="agency-wide meta performance across every account. currency is shown, never converted."
      />

      {optionsQuery.isError && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          couldn't load filter options — the reporting dataset may be empty or not yet reachable.
        </div>
      )}

      <section className="mb-4">
        <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">date range</h2>
        <DateRangePicker
          preset={preset}
          onPresetChange={setPreset}
          custom={custom}
          onCustomChange={setCustom}
        />
      </section>

      <section className="mb-6">
        <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">filters</h2>
        <ReportingFilterBar options={optionsQuery.data} selection={selection} onChange={setSelection} />
      </section>

      <p className="mb-4 text-sm text-slate-500">
        showing <span className="font-medium text-slate-700">{rangeLabel}</span>
        {filterCount > 0 && (
          <>
            {' '}
            · <span className="font-medium text-slate-700">{filterCount}</span> filter
            {filterCount === 1 ? '' : 's'} applied
          </>
        )}
        {asOf && <> · as of {new Date(asOf).toLocaleString()}</>}
      </p>

      {/* ⚠️ A converted figure must never be mistaken for a native one, so the
          disclosure sits with the scope line rather than tucked in a tooltip. */}
      <ConversionNote display={displayCurrency} snapshot={fxSnapshot} className="mb-4" />

      {/* ⚠️ Sits ABOVE the figures, like the portal's unmeasured-period banner: a
          caveat read after the number has already been believed is not a caveat. */}
      <PullFreshnessNote notice={freshnessNotice} className="mb-4" />

      {summaryQuery.isError && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          couldn't load reporting data for this range.
        </div>
      )}

      {/* §3b — unattributed + collision indicators for shared ad accounts. */}
      {rangeValid && (
        <AttributionHealthPanel health={healthQuery.data} isLoading={healthQuery.isLoading} />
      )}

      {/* Display-currency toggle — scopes the whole view (hero + charts) to one
          currency. Shown only for multi-currency scope; a single-currency scope
          just renders that currency. Client-side view state, no refetch. */}
      {/* ⚠️ The NATIVE per-currency toggle is hidden while a display currency is
          active. In converting mode the whole portfolio is in scope and every
          figure is already in one currency, so "which native currency am I
          looking at" is not a question that has an answer — leaving the toggle
          there would imply the view still scopes to one book. */}
      {!converting && rawSummary && showCurrencyToggle(rawSummary) && (
        <div className="mb-4 flex items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-400">
            display currency
          </span>
          <div className="flex gap-1.5">
            {rawSummary.currencies.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setPickedCurrency(c)}
                aria-pressed={c === nativeCurrency}
                className={cn(
                  'rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                  c === nativeCurrency
                    ? 'border-kyck-500 bg-kyck-50 text-kyck-700'
                    : 'border-slate-200 bg-white text-slate-500 hover:border-kyck-300',
                )}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Chart toolbar — daily/weekly/monthly grouping (re-buckets every chart,
          client-side) + the chart selector (which metric cards exist). Both
          persist. Shown once there's data to chart. */}
      {cards && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-400">grouping</span>
            <div className="inline-flex overflow-hidden rounded border border-slate-300" role="group" aria-label="Grouping">
              {TREND_GROUPINGS.map((g, i) => (
                <div key={g} className="flex">
                  {i > 0 && <span className="w-px bg-slate-300" aria-hidden />}
                  <button
                    type="button"
                    onClick={() => setGrouping(g)}
                    aria-pressed={chartsState.grouping === g}
                    className={cn(
                      'px-2 py-1 text-sm capitalize',
                      chartsState.grouping === g
                        ? 'bg-slate-100 font-medium text-slate-800'
                        : 'text-slate-500 hover:bg-slate-50',
                    )}
                  >
                    {GROUPING_LABEL[g]}
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* The agency display currency — Native (per-currency, the default)
                or convert everything at this month's rate. */}
            <DisplayCurrencySelect
              value={displayCurrency}
              onChange={setDisplayCurrency}
              snapshot={fxSnapshot}
              isAdmin={user.role === 'admin'}
            />
            <ReportingChartsMenu
              selected={selectedKeys}
              onChange={setSelectedCharts}
              multiTrend={chartsState.multiTrend}
              onMultiTrendChange={setMultiTrend}
            />
            {/* The canvas toolbar's other control. It sits beside the chart menu
                because both govern WHICH panels exist; the canvas below governs
                where they sit. */}
            <AddWidgetButton
              widgets={chartsState.widgets}
              onWidgetsChange={setWidgets}
              defaultMetricKey={DEFAULT_TREND_METRIC}
            />
            {/* The save model: which view you're on, Reset back to the team
                default, and (admins) make this view the team default. */}
            <ReportingViewActions
              gridKey={REPORTING_CHARTS_GRID_KEY}
              preferences={encodeChartsState(chartsState)}
              onReset={(next) =>
                setChartsState(decodeChartsState(next.gridPreferences?.[REPORTING_CHARTS_GRID_KEY]))
              }
            />
          </div>
        </div>
      )}

      {/* ── THE CANVAS ────────────────────────────────────────────────────────
          ONE grid for the whole top section: the selected metric cards (each a
          big-chart selector with its own style toggle), the trend chart, and any
          widgets the user has added. Every panel is freely placeable among the
          others — a single number top-left, a trend beside it, the chart
          underneath — rather than being confined to a region of its own kind.

          ⚠️ The single Reset now lives in the toolbar above (ReportingViewActions),
          not on the grid: since the save model landed it no longer means "put the
          panels back" but "discard my personal view and go back to the team
          default", which belongs beside the label saying which view you are on.
          The explorer below stays FIXED — it is the page's structural
          anchor, and a page whose anchor can be dragged away reads as broken.

          The loading skeleton stays a plain grid: there is nothing to arrange
          yet, and a draggable skeleton would be nonsense. */}
      {!(cards && scoped) && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {HERO_LABELS.map((label) => (
            <div key={label} className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="text-xs lowercase text-slate-500">{label}</div>
              <div className="mt-1 text-2xl font-semibold text-slate-300">
                {summaryQuery.isLoading ? '…' : '—'}
              </div>
              <div className="mt-3 h-16 rounded bg-slate-50" aria-hidden />
            </div>
          ))}
        </div>
      )}

      <ReportingChartGrid
        panels={canvasPanels}
        layout={chartsState.canvasLayout}
        onLayoutChange={(next) =>
          commitCharts({
            ...chartsState,
            // Merge rather than replace: a panel the user has hidden (an
            // unselected metric, a widget's metric switched away) keeps its
            // stored slot, so bringing it back lands where they left it.
            canvasLayout: mergeStoredLayout(
              chartsState.canvasLayout,
              next,
              reportingCanvasPanelKeys(chartsState.widgets),
            ),
          })
        }
      />

      {rawSummary && rawSummary.currencies.length === 0 && (
        <p className="mt-4 text-sm text-slate-400">
          no data for this range / filter selection.
        </p>
      )}

      {/* Campaign-name token filter — feeds the shared selection, so it scopes
          the WHOLE view (hero + table + health), not just the table below it. */}
      {rangeValid && (
        <section className="mb-3 mt-8">
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-400">
            name contains (filters all stats)
          </label>
          {/* Three levels, ANDed. ⚠️ An ad-set / ad filter deepens the SOURCE GRAIN
              of every query on the page, so the hero stats and the pivot narrow
              together — a campaign-grain KPI query would match no ad name at all
              and read zero while the table below still showed the matching ads. */}
          <div className="flex max-w-3xl flex-wrap items-start gap-2">
            <div className="min-w-[18rem] flex-1">
              <TokenChipInput
                value={selection.campaignSearchTokens}
                onChange={(tokens) => setSelection((s) => ({ ...s, campaignSearchTokens: tokens }))}
                placeholder="Campaign name contains…"
                ariaLabel="Campaign name contains"
              />
            </div>
            {/* The SAME chip input as the campaign box beside it — Enter commits
                a token, chips remove individually, tokens OR within the box.
                Three visually and behaviourally identical boxes. */}
            <div className="w-56">
              <TokenChipInput
                value={selection.adsetName}
                onChange={(tokens) => setSelection((s) => ({ ...s, adsetName: tokens }))}
                placeholder="Ad set name contains…"
                ariaLabel="Ad set name contains"
              />
            </div>
            <div className="w-56">
              <TokenChipInput
                value={selection.adName}
                onChange={(tokens) => setSelection((s) => ({ ...s, adName: tokens }))}
                placeholder="Ad name contains…"
                ariaLabel="Ad name contains"
              />
            </div>
          </div>
        </section>
      )}

      {/* Phase 3a — the drill-down explorer: the reporting page's one table.
          group-by Campaign reproduces the retired campaign table; add Account /
          Ad set / Ad for subtotal rollups. */}
      {rangeValid && (
        <BreakdownExplorer
          filters={filters}
          user={user}
          money={{ display: displayCurrency, snapshot: fxSnapshot }}
        />
      )}
    </div>
  );
}
